<template>
  <div class="border-b border-default flex last:border-0">
    <nuxt-link
      class="bg-main-product w-96 flex items-center cursor-pointer select-none"
      :class="{ 'opacity-50': loadingItem }"
      :to="itemRoute"
      @click="clickLink"
    >
      <div class="w-80 h-96 flex items-center rounded-regular overflow-hidden">
        <img
          v-if="item.isGiftCertificate"
          src="~/assets/images/ullmax_giftcard.jpg"
          class="w-full h-full object-cover"
          :alt="$lang('product_page', 'product_image_alt')"
          height="94"
          width="80"
        >
        <img
          v-else
          :src="item.image"
          class="w-full h-full object-cover"
          :alt="$lang('product_page', 'product_image_alt')"
          height="94"
          width="80"
        >
      </div>
    </nuxt-link>
    <div class="flex flex-col flex-1 pl-16">
      <div class="flex justify-between items-center mb-4">
        <nuxt-link
          class="reqular-text cursor-pointer select-none mr-8"
          :class="{ 'opacity-50': loadingItem }"
          :to="itemRoute"
          @click="clickLink"
        >
          {{ item.name }}
        </nuxt-link>
        <fa
          class="text-primary cursor-pointer select-none"
          :icon="['far', 'trash-alt']"
          :class="{ 'opacity-50': loadingItem }"
          @click="deleteFromCart"
        />
      </div>
      <div class="text-size-chart text-muted leading-xxs mb-8">
        {{ itemVersion }}
      </div>
      <div class="flex justify-between items-center mt-auto">
        <div
          v-if="!item.isGiftCertificate"
          class="inline-block w-96"
          :class="{ 'opacity-50': loadingSelector }"
        >
          <div class="flex items-center rounded-sm justify-between text-sm">
            <div
              class="cursor-pointer select-none text-sm h-32 w-32 flex justify-center items-center bg-secondary text-primary rounded-full"
              @click="decreaseQuantity"
            >
              <fa :icon="['far', 'minus']" />
            </div>
            {{ count }}
            <div
              class="cursor-pointer select-none text-sm h-32 w-32 flex justify-center items-center bg-secondary text-primary rounded-full"
              @click="increaseQuantity"
            >
              <fa :icon="['far', 'plus']" />
            </div>
          </div>
        </div>
        <div v-if="item.showAsOnSale" class="flex flex-col items-end">
          <div
            class="text-price text-warning float-right"
            :class="{ 'opacity-50': loadingItem }"
          >
            {{ item.totalPrice }}
          </div>
          <div class="text-size-chart text-black-50 line-through float-right">
            {{ item.originalPrice }}
          </div>
        </div>
        <div
          v-else
          class="text-price float-right ml-auto"
          :class="{ 'opacity-50': loadingItem }"
        >
          {{ item.totalPrice }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type CartItem from '~/models/cart-item';
import { useConfigStore } from '~/store/config';
import type { EcomProductItem } from '~/constants/types/product';
import debounce from '~/util/debounce';
import { useUiStore } from '~/store/ui';

import { useCartStore } from '~/store/cart';
import { pushProductClick } from '~/composables/useTracking';
const config = useConfigStore();

const uiStore = useUiStore();
const cartStore = useCartStore();

const props = defineProps<{
  item: CartItem,
  isCheckout: boolean,
}>();

const emit = defineEmits<{
  (e: 'loadingPrice', payload: boolean): void;
  (e: 'klarnasuspend'): void;
  (e: 'klarnaresume'): void;
}>();

const loadingItem = ref(false);
const loadingSelector = ref(false);
const count = ref(props.item.quantity);
const modifyQuantityDebounced = ref<any>(null);

const itemRoute = computed(()=> {
  return config.getProductRoute(props.item.product.ecomId)?.route || '';
});

const currentItemVersion = computed<EcomProductItem | null>(()=> {
  return props.item.product.items.find((item) => item.item === props.item.item) || null;
});
const itemVersion = computed(()=> {
  const color = props.item.product.color?.desc?.trim();

  if (currentItemVersion.value && !props.item.product.hideSizeChoice) {
    if (!props.item.product.hideColorChoice) {
      return `${color}, ${currentItemVersion.value.name}`;
    } else {
      return `${currentItemVersion.value.name}`;
    }
  } else if (!props.item.product.hideColorChoice) {
    return color;
  } else {
    return '';
  }
});

onMounted(()=> {
  modifyQuantityDebounced.value = debounce(modifyCartQuantity, 1000);
});

const decreaseQuantity = () => {
  if (count.value > 0) {
    emit('loadingPrice', true);
    loadingItem.value = true;
    count.value = --count.value;
    modifyQuantityDebounced.value();
  }
};

const increaseQuantity = () => {
  emit('loadingPrice', true);
  loadingItem.value = true;
  count.value = ++count.value;
  modifyQuantityDebounced.value();
};

const modifyCartQuantity = async() => {
  try {
    emit('klarnasuspend');
    const itemQuantity = Math.abs(props.item.quantity - count.value);
    const originalValue = props.item.quantity;

    await cartStore.modifyItemQuantity({
      line: props.item.line,
      quantity: count.value,
      item: props.item,
    });
  } catch (e) {
    console.log(e);
    //uiStore.setError(e.response.status);
    uiStore.setError('?');
  } finally {
    loadingItem.value = false;
    emit('loadingPrice', false);
    emit('klarnaresume');
  }
};

const deleteFromCart = () => {
  cartStore.deleteFromCart({ line: props.item.line, item: props.item });
};

const clickLink = () => {
  pushProductClick(props.item.product, 'Checkout');
};

</script>

<style scoped lang="postcss">
</style>
